import React, { useMemo, useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * <DataTable
 *   columns={[
 *     { key:'name',   label:'Name',   sortable:true,
 *       render:r => <span className="font-medium">{r.name}</span> },
 *     { key:'amount', label:'Amount', align:'right', sortable:true,
 *       render:r => formatCurrency(r.amount) },
 *   ]}
 *   rows={rows}
 *   rowKey={r => r.id}
 *   emptyMessage="No records yet."
 *   pageSize={10}
 *   onRowClick={r => ...}
 * />
 */
const DataTable = ({
    columns = [],
    rows = [],
    rowKey = (r, i) => i,
    emptyMessage = 'No records.',
    pageSize = 25,
    onRowClick,
    density = 'comfortable',    // 'compact' | 'comfortable'
    stickyHeader = false,
}) => {
    const [sort, setSort] = useState(null);   // {key, dir: 'asc'|'desc'}
    const [page, setPage] = useState(0);

    const sortedRows = useMemo(() => {
        if (!sort) return rows;
        const col = columns.find(c => c.key === sort.key);
        if (!col) return rows;
        const dir = sort.dir === 'asc' ? 1 : -1;
        const getter = col.sortValue || (r => r[sort.key]);
        return [...rows].sort((a, b) => {
            const va = getter(a); const vb = getter(b);
            if (va == null && vb == null) return 0;
            if (va == null) return 1;
            if (vb == null) return -1;
            if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
            return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir;
        });
    }, [rows, sort, columns]);

    const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
    const currentPage = Math.min(page, totalPages - 1);
    const start = currentPage * pageSize;
    const paged = sortedRows.slice(start, start + pageSize);

    const toggleSort = (key) => {
        setSort(cur => {
            if (!cur || cur.key !== key) return { key, dir: 'asc' };
            if (cur.dir === 'asc') return { key, dir: 'desc' };
            return null;
        });
    };

    const cellPad = density === 'compact' ? 'py-2 px-3' : 'py-3 px-4';

    return (
        <div className="w-full">
            {/* On narrow screens the table scrolls sideways rather than squeezing
                columns until values wrap or clip. min-w keeps columns readable. */}
            <div className="w-full overflow-x-auto [scrollbar-width:thin]">
                <table className="w-full min-w-[44rem] text-sm">
                    <thead className={`${stickyHeader ? 'sticky top-0 z-10' : ''} bg-neutral-50 text-left border-b border-neutral-200/70`}>
                        <tr>
                            {columns.map(c => {
                                const active = sort?.key === c.key;
                                return (
                                    <th key={c.key}
                                        style={{ width: c.width }}
                                        className={`${cellPad} font-medium text-xs uppercase tracking-wide text-neutral-500
                                                    ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : 'text-left'}
                                                    ${c.sortable ? 'cursor-pointer select-none hover:text-neutral-900' : ''}`}
                                        onClick={c.sortable ? () => toggleSort(c.key) : undefined}>
                                        <span className={`inline-flex items-center gap-1 ${c.align === 'right' ? 'flex-row-reverse' : ''}`}>
                                            {c.label}
                                            {c.sortable && (
                                                active
                                                    ? (sort.dir === 'asc'
                                                        ? <ArrowUp className="h-3 w-3 text-primary-700" />
                                                        : <ArrowDown className="h-3 w-3 text-primary-700" />)
                                                    : <ArrowUpDown className="h-3 w-3 text-neutral-300" />
                                            )}
                                        </span>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {paged.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className={`${cellPad} text-center text-neutral-400 py-12`}>
                                    {emptyMessage}
                                </td>
                            </tr>
                        ) : paged.map((r, i) => (
                            <tr key={rowKey(r, start + i)}
                                onClick={onRowClick ? () => onRowClick(r) : undefined}
                                className={`border-b border-neutral-100 last:border-b-0
                                            ${onRowClick ? 'cursor-pointer hover:bg-neutral-50' : ''}`}>
                                {columns.map(c => (
                                    <td key={c.key}
                                        className={`${cellPad} text-neutral-700
                                                    ${c.align === 'right' ? 'text-right tabular-nums' : c.align === 'center' ? 'text-center' : ''}
                                                    ${c.mono ? 'font-mono text-xs' : ''}`}>
                                        {c.render ? c.render(r) : (r[c.key] ?? '—')}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {sortedRows.length > pageSize && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-neutral-200/70 text-xs text-neutral-500">
                    <span>
                        Showing <span className="font-medium text-neutral-900">{start + 1}–{Math.min(start + pageSize, sortedRows.length)}</span> of <span className="font-medium text-neutral-900">{sortedRows.length}</span>
                    </span>
                    <div className="flex items-center gap-1">
                        <button onClick={() => setPage(p => Math.max(0, p - 1))}
                                disabled={currentPage === 0}
                                className="p-1.5 rounded-md hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed">
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <span className="tabular-nums px-2">
                            {currentPage + 1} / {totalPages}
                        </span>
                        <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={currentPage >= totalPages - 1}
                                className="p-1.5 rounded-md hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed">
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataTable;
